import { Component } from '@theme/component';

/**
 * A full width desktop navigation panel that is opened from the header "MENU" button.
 *
 * The panel replaces the classic horizontal navigation on desktop: every top level link of the
 * selected menu becomes a column, and the panel additionally holds a featured image tile and a
 * row of business links.
 *
 * @typedef {object} Refs
 * @property {HTMLButtonElement} trigger - The button that opens and closes the panel.
 * @property {HTMLElement} panel - The panel element.
 * @property {HTMLElement} [inner] - The scrollable content of the panel.
 *
 * @extends {Component<Refs>}
 */
class KrosnoMenuPanel extends Component {
  requiredRefs = ['trigger', 'panel'];

  /**
   * The header the panel belongs to. Kept as a property so the open state can still be cleaned up
   * once the element has been removed from the DOM.
   * @type {HTMLElement | null}
   */
  #header = null;

  /**
   * Listeners that only exist while the panel is open.
   * @type {AbortController | null}
   */
  #listeners = null;

  connectedCallback() {
    super.connectedCallback();

    this.#header = this.closest('header-component');
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#stopListening();
    this.#header?.removeAttribute('data-kmp-open');
  }

  /** @returns {boolean} Whether the panel is currently open. */
  get isOpen() {
    return this.hasAttribute('open');
  }

  /**
   * Toggles the panel.
   * @param {Event} [event] - The event that triggered the toggle.
   */
  toggle = (event) => {
    event?.preventDefault();

    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  };

  /** Opens the panel. */
  open = () => {
    if (this.isOpen) return;

    this.setAttribute('open', '');
    this.refs.trigger.setAttribute('aria-expanded', 'true');
    this.#header?.setAttribute('data-kmp-open', '');

    const controller = new AbortController();
    const { signal } = controller;

    this.#listeners = controller;

    this.#updateAvailableHeight();

    document.addEventListener('keydown', this.#handleKeydown, { signal });
    document.addEventListener('pointerdown', this.#handlePointerDown, { signal });
    document.addEventListener('focusin', this.#handleFocusIn, { signal });
    window.addEventListener('scroll', this.close, { signal, passive: true });
    window.addEventListener('resize', this.#updateAvailableHeight, { signal, passive: true });
  };

  /**
   * The panel hangs below the header row, whose height depends on the announcement bar and on the
   * sticky state. Measuring it is more reliable than deriving it from `--header-group-height`,
   * which the theme rounds down elsewhere.
   */
  #updateAvailableHeight = () => {
    const panel = this.refs.panel;
    if (!panel) return;

    const available = window.innerHeight - panel.getBoundingClientRect().top - panel.clientTop;

    this.style.setProperty('--kmp-max-height', `${Math.round(Math.max(240, available))}px`);

    // Na niskich ekranach panel może się nie zmieścić mimo zagęszczenia rytmu.
    // Wtedy dolna krawędź dostaje wygaszenie, żeby było widać, że treść trwa dalej.
    // Odczyt `scrollHeight` sam wymusza przeliczenie układu po ustawieniu
    // `--kmp-max-height`, więc pomiar jest aktualny bez czekania na klatkę.
    const inner = this.refs.inner;

    if (inner) {
      this.toggleAttribute('data-scrollable', inner.scrollHeight - inner.clientHeight > 2);
    }
  };

  /**
   * Closes the panel.
   * @param {Event} [event] - The event that triggered the close.
   */
  close = (event) => {
    event?.preventDefault();

    if (!this.isOpen) return;

    this.removeAttribute('open');
    this.refs.trigger.setAttribute('aria-expanded', 'false');
    this.#header?.removeAttribute('data-kmp-open');
    this.#stopListening();
  };

  #stopListening() {
    this.#listeners?.abort();
    this.#listeners = null;
  }

  /** @param {KeyboardEvent} event */
  #handleKeydown = (event) => {
    if (event.key !== 'Escape') return;

    this.close();
    this.refs.trigger.focus();
  };

  /** @param {PointerEvent} event */
  #handlePointerDown = (event) => {
    if (event.composedPath().includes(this)) return;

    this.close();
  };

  /** @param {FocusEvent} event */
  #handleFocusIn = (event) => {
    const target = event.target;

    if (target instanceof Node && this.contains(target)) return;

    this.close();
  };
}

if (!customElements.get('krosno-menu-panel')) {
  customElements.define('krosno-menu-panel', KrosnoMenuPanel);
}
